import {
  BASE_PATHS,
  PATHS,
  useRouter,
  trackMixpanelEvent,
} from '@belong/common';
import { IconClose, IconMenu } from '@belong/icons';
import { Image, LinkBase, Link, ButtonBase, Text } from '@belong/ui';
import clsx from 'clsx';
import { useModal } from 'src/hooks/useModal';
import { FlowHeaderMobile } from './flow-header-mobile';
import { FlowHeaderMobileTip } from './flow-header-mobile-tip';
import css from './flow-header.module.css';

type FlowHeaderProps = {
  exitHref?: string;
  title?: string;
  description?: string | string[];
  content?: React.ReactNode;
  shouldHideBurgerMenu?: boolean;
  whiteThemeRoutes?: string[];
};

export function FlowHeader({
  exitHref = BASE_PATHS.ACCOUNTS,
  title,
  description,
  content,
  shouldHideBurgerMenu = false,
  whiteThemeRoutes = [],
}: FlowHeaderProps) {
  const [isOverlayOpen, openOverlayModal, closeOverlayModal] = useModal();
  const [isTipsOpen, openTipsModal, closeTipsModal] = useModal();
  const { pathname } = useRouter();

  const isWhiteRoute = whiteThemeRoutes.some((route) =>
    pathname.includes(route),
  );

  const handleExitClick = () => {
    if (pathname.includes('/rent-estimate/')) {
      trackMixpanelEvent('flow-exited', {
        flowType: 'RentEstimate',
        stepEnum: pathname.split('/').pop() || 'unknown',
      });
    }
  };

  return (
    <header
      className={clsx('h-header relative shrink-0', { 'z-10': isWhiteRoute })}
    >
      <div className="container relative flex h-full items-center justify-between">
        <LinkBase href="/" className={css.logoLink} aria-label="Go To Homepage">
          {isWhiteRoute ? (
            <Image
              src="/logos/belong_logo_web_white.svg"
              alt="Belong logo"
              aria-hidden="true"
              className="block"
            />
          ) : (
            <Image
              src="/logos/belong_logo_web.svg"
              alt="Belong logo"
              aria-hidden="true"
              className="block"
            />
          )}
        </LinkBase>
        <section className="flex items-center md:hidden">
          {!!title?.length && !isTipsOpen && !isOverlayOpen && (
            <ButtonBase
              onClick={openTipsModal}
              aria-label="Tips Menu"
              style={{ height: 28 }}
            >
              <Image src="/icons/light-bulb.svg" alt="" />
            </ButtonBase>
          )}

          {!isOverlayOpen && !isTipsOpen && !shouldHideBurgerMenu && (
            <ButtonBase
              onClick={openOverlayModal}
              aria-label="Burger Menu"
              className={`ml-md${isWhiteRoute ? ' text-white' : ''}`}
            >
              <IconMenu width={24} height={18} aria-hidden="true" />
            </ButtonBase>
          )}

          {(isTipsOpen || isOverlayOpen) && (
            <ButtonBase
              onClick={() => {
                closeOverlayModal();
                closeTipsModal();
              }}
            >
              <IconClose width={16} />
            </ButtonBase>
          )}

          <FlowHeaderMobile
            isOverlayOpen={isOverlayOpen}
            closeOverlayModal={closeOverlayModal}
            exitHref={exitHref}
          />

          {!!title?.length && (
            <FlowHeaderMobileTip
              isOverlayOpen={isTipsOpen}
              closeOverlayModal={closeTipsModal}
              description={description}
              title={title}
              content={content}
            />
          )}
        </section>

        <section className="hidden items-center md:flex">
          <div className="mr-sm flex">
            <Link href={PATHS.SUPPORT}>
              <Text
                variant="p1"
                fontWeight="semibold"
                className={isWhiteRoute ? 'text-white' : 'text-navy'}
              >
                GET HELP
              </Text>
            </Link>
          </div>

          <div className="flex">
            <Link href={exitHref} onClick={handleExitClick}>
              <Text
                variant="p1"
                fontWeight="semibold"
                className={isWhiteRoute ? 'text-white' : 'text-navy'}
              >
                EXIT
              </Text>
            </Link>
          </div>
        </section>
      </div>
    </header>
  );
}
